class TeaDucksHome extends HTMLElement {
  connectedCallback() {
    this.abortController?.abort();
    this.landingController?.abort();
    this.abortController = new AbortController();
    this.inFlight = false;
    this.cartOperation = 0;
    this.products = this.readProducts();
    this.schedules = this.readSchedules();
    this.state ||= {
      flavour: 'floral',
      tea: 'moonlight-white',
      format: this.dataset.defaultFormat || 'caddy',
      planId: '',
      frequency: 'month',
    };
    this.cacheElements();
    this.restoreSelectorChoice();
    this.bindEvents();
    this.ensureSelectedMarks();
    this.updateSelector();
    if (window.location.hash === '#tea-selector') this.enhanceSelectorLanding();
  }

  disconnectedCallback() {
    this.abortController?.abort();
    this.landingController?.abort();
  }

  readProducts() {
    const source = this.querySelector('[data-selector-products]');
    if (!(source instanceof HTMLScriptElement)) return [];
    try {
      const products = JSON.parse(source.textContent || '[]');
      return Array.isArray(products) ? products : [];
    } catch (error) {
      console.error('[Tea Ducks] Selector product data is invalid.', error);
      return [];
    }
  }

  readSchedules() {
    const source = this.querySelector('[data-selling-plan-schedules]');
    try {
      const schedules = JSON.parse(source?.textContent || '[]');
      return Array.isArray(schedules) ? schedules : [];
    } catch {
      return [];
    }
  }

  ensureSelectedMarks() {
    const mark = this.querySelector('.td-selector__selected-mark');
    if (!mark) return;
    for (const button of this.querySelectorAll('[data-tea]')) {
      if (!button.querySelector('.td-selector__selected-mark')) button.append(mark.cloneNode(true));
    }
  }

  cacheElements() {
    this.form = this.querySelector('[data-selector-form]');
    this.image = this.querySelector('[data-selector-image], .td-selector__image');
    this.imagePlaceholder = this.querySelector('[data-selector-image-placeholder]');
    this.nameOutput = this.querySelector('[data-selector-name]');
    this.notesOutput = this.querySelector('[data-selector-notes]');
    this.detailsLink = this.querySelector('[data-selector-details]');
    this.recipeOutputs = {
      leafAmountG: this.querySelector('[data-selector-leaf]'),
      waterMl: this.querySelector('[data-selector-water]'),
      temperature: this.querySelector('[data-selector-temperature]'),
      timeMin: this.querySelector('[data-selector-time]'),
    };
    this.selectorSection = this.querySelector('#tea-selector');
    this.planSelect = this.querySelector('[data-plan-select]');
    this.oncePrice = this.querySelector('[data-one-time-price]');
    this.subscriptionPrice = this.querySelector('[data-subscription-price]');
    this.status = this.querySelector('[data-purchase-status]');
    this.purchaseButtons = [...this.querySelectorAll('[data-purchase]')];
    this.giftRail = this.querySelector('.td-home__gift-rail');
  }

  bindEvents() {
    const {signal} = this.abortController;
    this.addEventListener('error', event => {
      const image = event.target;
      if (!(image instanceof HTMLImageElement)) return;
      if (image === this.image) {
        image.hidden = true;
        if (this.imagePlaceholder) this.imagePlaceholder.hidden = false;
      } else if (image.matches('.td-selector__packaging-image')) {
        image.hidden = true;
        const placeholder = image.parentElement.querySelector('[data-card-placeholder]');
        if (placeholder) placeholder.hidden = false;
      }
    }, {capture: true, signal});
    this.addEventListener('load', event => {
      if (event.target === this.image && this.image.naturalWidth > 0) {
        this.image.hidden = false;
        if (this.imagePlaceholder) this.imagePlaceholder.hidden = true;
      }
    }, {capture: true, signal});
    this.addEventListener('click', (event) => this.handleChoice(event), {signal});
    window.addEventListener('pagehide', () => this.saveSelectorChoice(), {signal});
    document.addEventListener('td:market-switch', () => this.saveSelectorChoice(), {signal});
    window.addEventListener('hashchange', () => {
      if (window.location.hash === '#tea-selector') this.enhanceSelectorLanding();
    }, {signal});
    document.addEventListener('click', event => {
      const link = event.target instanceof Element ? event.target.closest('a[href]') : null;
      if (!(link instanceof HTMLAnchorElement) || this.contains(link) || event.defaultPrevented || event.button > 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
      const url = new URL(link.href, window.location.href);
      if (url.origin === window.location.origin && url.pathname === window.location.pathname && url.hash === '#tea-selector') this.enhanceSelectorLanding();
    }, {signal});
    this.form?.addEventListener('submit', (event) => this.handleSubmit(event), {signal});
    this.giftRail?.addEventListener('keydown', (event) => this.handleGiftRailKey(event), {signal});
    this.planSelect?.addEventListener('change', () => {
      this.state.planId = this.planSelect.value;
      this.state.frequency = this.planSelect.selectedOptions[0]?.dataset.frequency || '';
      this.updatePricesAndAvailability();
      this.saveSelectorChoice();
    }, {signal});
  }

  selectorChoiceKey() {
    const market = this.dataset.country === 'HK' ? ':HK' : '';
    return `td-selector-choice:${window.Shopify?.theme?.id || 'draft'}${market}`;
  }

  saveSelectorChoice() {
    try {
      const {tea, format, flavour, frequency} = this.state;
      const choice = JSON.stringify({tea, format, flavour, frequency, updatedAt: Date.now()});
      sessionStorage.setItem(this.selectorChoiceKey(), choice);
      sessionStorage.setItem(`td-selector-transfer:${window.Shopify?.theme?.id || 'draft'}`, choice);
      // A native new-tab link has its own session storage. Retain only this
      // functional preference briefly across tabs; never cart or customer data.
      localStorage.setItem(this.selectorChoiceKey(), choice);
    } catch { /* Storage is optional; native links and current-page state remain usable. */ }
  }

  restoreSelectorChoice() {
    if (window.location.hash !== '#tea-selector') return;
    try {
      let choice = JSON.parse(sessionStorage.getItem(this.selectorChoiceKey()) || 'null');
      if (!choice) choice = JSON.parse(sessionStorage.getItem(`td-selector-transfer:${window.Shopify?.theme?.id || 'draft'}`) || 'null');
      if (!choice) {
        const shared = JSON.parse(localStorage.getItem(this.selectorChoiceKey()) || 'null');
        if (shared && Date.now() - shared.updatedAt < 30 * 60 * 1000) choice = shared;
      }
      if (!choice || !['caddy', 'rounds'].includes(choice.format)) return;
      if (!this.products.some(product => product.key === choice.tea && product.format === choice.format && (this.dataset.country !== 'HK' || product.available))) {
        if (this.dataset.country !== 'HK' || !this.products.some(product => product.key === choice.tea && product.format === this.dataset.defaultFormat && product.available)) return;
        choice.format = this.dataset.defaultFormat;
        if (this.status) this.status.textContent = this.dataset.formatAdjusted || '';
      }
      const teaButton = this.querySelector(`[data-tea="${CSS.escape(choice.tea)}"]`);
      if (!(teaButton instanceof HTMLButtonElement)) return;
      const flavours = (teaButton.dataset.flavours || '').split(' ');
      const flavour = flavours.includes(choice.flavour) ? choice.flavour : 'all';
      this.state = {...this.state, tea: choice.tea, format: choice.format, flavour, frequency: ['month', 'week', 'two_weeks'].includes(choice.frequency) ? choice.frequency : 'month', planId: ''};
    } catch { /* An invalid or unavailable saved choice never replaces the approved defaults. */ }
  }

  enhanceSelectorLanding() {
    void this.landAtSelector().catch(error => {
      console.warn('[Tea Ducks] Selector positioning enhancement unavailable; native anchor retained.', error);
    });
  }

  handleGiftRailKey(event) {
    if (event.target !== this.giftRail || !['ArrowLeft', 'ArrowRight', 'Home', 'End'].includes(event.key)) return;
    event.preventDefault();
    const card = this.giftRail.querySelector('.td-home__gift-card');
    const step = (card?.getBoundingClientRect().width || 260) + parseFloat(getComputedStyle(this.giftRail).columnGap || '0');
    if (event.key === 'Home') this.giftRail.scrollLeft = 0;
    else if (event.key === 'End') this.giftRail.scrollLeft = this.giftRail.scrollWidth;
    else this.giftRail.scrollLeft += event.key === 'ArrowRight' ? step : -step;
  }

  handleChoice(event) {
    if (!(event.target instanceof Element)) return;
    const selectorJump = event.target.closest('[data-selector-jump]');
    if (selectorJump instanceof HTMLAnchorElement) {
      if (event.defaultPrevented || event.button > 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
      if (!this.selectorSection) return;
      void this.landAtSelector().catch(error => {
        console.warn('[Tea Ducks] Selector positioning enhancement unavailable; native anchor retained.', error);
      });
      return;
    }
    const flavourButton = event.target.closest('[data-flavour]');
    const formatButton = event.target.closest('[data-format]');
    const teaButton = event.target.closest('[data-tea]');

    if (flavourButton instanceof HTMLButtonElement) {
      this.state.flavour = flavourButton.dataset.flavour || 'all';
      const selectedTeaButton = this.querySelector(`[data-tea="${CSS.escape(this.state.tea)}"]`);
      const selectedStillValid = selectedTeaButton instanceof HTMLButtonElement
        && (selectedTeaButton.dataset.flavours || '').split(' ').includes(this.state.flavour);
      if (!selectedStillValid) {
        const firstValid = [...this.querySelectorAll('[data-tea]')].find((button) => {
          return button instanceof HTMLButtonElement
            && (button.dataset.flavours || '').split(' ').includes(this.state.flavour);
        });
        if (firstValid instanceof HTMLButtonElement) this.state.tea = firstValid.dataset.tea || this.state.tea;
      }
      this.updateSelector();
      return;
    }

    if (formatButton instanceof HTMLButtonElement) {
      this.state.format = formatButton.dataset.format || 'caddy';
      this.updateSelector();
      return;
    }

    if (teaButton instanceof HTMLButtonElement) {
      this.state.tea = teaButton.dataset.tea || this.state.tea;
      this.updateSelector();
    }
  }

  currentProduct() {
    return this.products.find((product) => product.key === this.state.tea && product.format === this.state.format);
  }

  updateSelector() {
    const product = this.currentProduct();
    if (this.dataset.country === 'HK') {
      for (const button of this.querySelectorAll('[data-format]')) {
        const eligible = this.products.some(item => item.format === button.dataset.format && item.available);
        button.hidden = !eligible;
        button.disabled = !eligible;
      }
    }
    this.updatePressedStates();
    this.updateVisibleTeas();
    this.updateTeaQuantities();
    this.updateCardImages();
    this.updateRecipe(product);

    if (!product) {
      this.renderUnavailable('This tea and format combination is not configured.');
      return;
    }

    if (this.nameOutput) this.nameOutput.textContent = product.displayName;
    if (this.notesOutput) this.notesOutput.textContent = product.tastingNotes;
    if (this.detailsLink instanceof HTMLAnchorElement) this.detailsLink.href = product.productUrl;
    this.updateImage(product);
    this.updatePlanOptions(product);
    this.updatePricesAndAvailability();
    this.revealSelectedHorizontalChoices();
    this.saveSelectorChoice();
  }

  updateRecipe(product) {
    const recipe = product?.brewing || {};
    const suffixes = {leafAmountG: 'g', waterMl: 'ml', temperature: '', timeMin: ' min'};
    for (const [key, output] of Object.entries(this.recipeOutputs || {})) {
      if (!output) continue;
      const value = recipe[key];
      output.textContent = value === null || value === undefined || value === '' ? '—' : `${value}${suffixes[key]}`;
    }
  }

  selectorScrollContainer() {
    let element = this.selectorSection?.parentElement;
    while (element && element !== document.body && element !== document.documentElement) {
      if (/(auto|scroll)/.test(getComputedStyle(element).overflowY) && element.scrollHeight > element.clientHeight) return element;
      element = element.parentElement;
    }
    return window;
  }

  selectorLanding() {
    const section = this.selectorSection;
    const heading = section?.querySelector('h2');
    if (!section || !heading) return null;
    const viewport = window.visualViewport;
    const viewportTop = viewport?.offsetTop || 0;
    const viewportHeight = viewport?.height || window.innerHeight;
    const scroller = this.selectorScrollContainer();
    const rootBox = scroller === window ? null : scroller.getBoundingClientRect();
    const visibleTop = Math.max(viewportTop, rootBox ? rootBox.top + scroller.clientTop : 0);
    let visibleBottom = Math.min(viewportTop + viewportHeight, rootBox ? rootBox.top + scroller.clientTop + scroller.clientHeight : viewportTop + viewportHeight);
    const previewBar = document.querySelector('#PBarNextFrameWrapper');
    if (previewBar && getComputedStyle(previewBar).position === 'fixed') {
      const barBox = previewBar.getBoundingClientRect();
      if (barBox.height > 0 && barBox.bottom >= visibleBottom && barBox.top > visibleTop) visibleBottom = Math.min(visibleBottom, barBox.top);
    }
    let headerInset = visibleTop;
    for (const header of document.querySelectorAll('.td-header, header-component, [data-sticky-header]')) {
      let owner = header;
      while (owner && !['fixed', 'sticky'].includes(getComputedStyle(owner).position)) owner = owner.parentElement;
      if (!owner) continue;
      const style = getComputedStyle(owner);
      const box = owner.getBoundingClientRect();
      if (style.visibility === 'hidden' || style.display === 'none' || Number(style.opacity) === 0) continue;
      if (box.top <= visibleTop + 1 && box.bottom > visibleTop) headerInset = Math.max(headerInset, box.bottom);
    }
    const gap = window.matchMedia('(min-width: 990px)').matches ? 0 : 16;
    const headingBox = heading.getBoundingClientRect();
    const scrollTop = scroller === window ? window.scrollY : scroller.scrollTop;
    const headingTop = Math.max(section.getBoundingClientRect().top, headingBox.top) + scrollTop;
    const buttonsBottom = Math.max(headingBox.bottom, ...this.purchaseButtons.map(button => button.getBoundingClientRect().bottom));
    const requiredHeight = buttonsBottom - headingBox.top;
    const availableHeight = Math.max(0, visibleBottom - headerInset - gap);
    return {
      top: Math.max(0, headingTop - headerInset - gap),
      scroller,
      heading,
      fits: requiredHeight <= availableHeight,
      availableHeight,
      requiredHeight,
    };
  }

  waitForSelectorLayout(signal) {
    const media = [...this.querySelectorAll('.td-home__hero img, [data-selector-image]')];
    const pending = [document.fonts?.ready, ...media.map(image => image.decode?.().catch(() => {}))].filter(Boolean);
    return new Promise(resolve => {
      let frame;
      let completed = false;
      let framing = false;
      const finish = ready => {
        if (completed) return;
        completed = true;
        clearTimeout(timer);
        if (frame) cancelAnimationFrame(frame);
        signal.removeEventListener('abort', cancel);
        resolve(ready);
      };
      const cancel = () => finish(false);
      const ready = () => {
        if (completed || framing || signal.aborted) return;
        framing = true;
        clearTimeout(timer);
        frame = requestAnimationFrame(() => { frame = requestAnimationFrame(() => finish(true)); });
      };
      const timer = setTimeout(ready, 2500);
      signal.addEventListener('abort', cancel, {once: true});
      Promise.allSettled(pending).then(ready);
      if (signal.aborted) cancel();
    });
  }

  async landAtSelector() {
    this.landingController?.abort();
    const controller = new AbortController();
    this.landingController = controller;
    const {signal} = controller;
    for (const type of ['wheel', 'touchstart', 'pointerdown', 'keydown']) {
      window.addEventListener(type, () => controller.abort(), {once: true, passive: true, signal});
    }
    if (!await this.waitForSelectorLayout(signal) || signal.aborted) return;
    const landing = this.selectorLanding();
    if (!landing) { controller.abort(); return; }
    const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    landing.heading.focus({preventScroll: true});
    landing.scroller.scrollTo({top: landing.top, behavior: reduceMotion ? 'instant' : 'smooth'});
    let previousTop = landing.top;
    let frame;
    const correct = () => {
      if (signal.aborted) return;
      cancelAnimationFrame(frame);
      frame = requestAnimationFrame(() => {
        if (signal.aborted) return;
        const next = this.selectorLanding();
        if (!next || Math.abs(next.top - previousTop) <= 1) return;
        previousTop = next.top;
        next.scroller.scrollTo({top: next.top, behavior: 'instant'});
      });
    };
    const observer = typeof ResizeObserver === 'function' ? new ResizeObserver(correct) : null;
    for (const element of [this, this.selectorSection, landing.heading, this.form, landing.scroller === window ? null : landing.scroller]) {
      if (element) observer?.observe(element);
    }
    document.fonts?.ready.then(correct, () => {});
    landing.scroller.addEventListener('scrollend', correct, {signal});
    window.addEventListener('resize', correct, {signal});
    window.visualViewport?.addEventListener('resize', correct, {signal});
    const timer = setTimeout(() => controller.abort(), 3000);
    signal.addEventListener('abort', () => {
      clearTimeout(timer);
      cancelAnimationFrame(frame);
      observer?.disconnect();
    }, {once: true});
  }

  revealSelectedHorizontalChoices() {
    if (!window.matchMedia('(max-width: 767px)').matches) return;
    for (const selector of ['[data-flavour-controls]', '[data-tea-controls]']) {
      const rail = this.querySelector(selector);
      const selected = rail?.querySelector('[aria-pressed="true"]');
      if (!rail || !selected || rail.scrollWidth <= rail.clientWidth) continue;
      const railBox = rail.getBoundingClientRect();
      const selectedBox = selected.getBoundingClientRect();
      if (selectedBox.left < railBox.left) rail.scrollLeft += selectedBox.left - railBox.left - 3;
      else if (selectedBox.right > railBox.right) rail.scrollLeft += selectedBox.right - railBox.right + 3;
    }
  }

  updatePressedStates() {
    for (const button of this.querySelectorAll('[data-flavour]')) {
      button.setAttribute('aria-pressed', String(button.getAttribute('data-flavour') === this.state.flavour));
    }
    for (const button of this.querySelectorAll('[data-format]')) {
      button.setAttribute('aria-pressed', String(button.getAttribute('data-format') === this.state.format));
    }
    for (const button of this.querySelectorAll('[data-tea]')) {
      button.setAttribute('aria-pressed', String(button.getAttribute('data-tea') === this.state.tea));
    }
  }

  updateVisibleTeas() {
    for (const button of this.querySelectorAll('[data-tea]')) {
      const flavours = (button.getAttribute('data-flavours') || '').split(' ');
      const eligible = this.dataset.country !== 'HK' || this.products.some(item => item.key === button.dataset.tea && item.format === this.state.format && item.available);
      button.hidden = !flavours.includes(this.state.flavour) || !eligible;
    }
  }

  updateTeaQuantities() {
    for (const button of this.querySelectorAll('[data-tea]')) {
      const key = button.getAttribute('data-tea');
      const product = this.products.find((item) => item.key === key && item.format === this.state.format);
      const output = button.querySelector('[data-tea-quantity]');
      if (output) output.textContent = product?.quantityLabel || 'Unavailable';
    }
  }

  updateCardImages() {
    for (const media of this.querySelectorAll('[data-card-format]')) {
      media.hidden = media.dataset.cardFormat !== this.state.format;
    }
  }

  updateImage(product) {
    if (!(this.image instanceof HTMLImageElement)) return;
    // Leaf detail belongs to the tea, never to its purchasable format.
    const template = this.querySelector(`template[data-selector-leaf-media="${CSS.escape(product.key)}"]`);
    const source = template?.content?.querySelector('img');
    if (source) {
      for (const name of ['src', 'srcset', 'sizes', 'alt', 'width', 'height', 'style']) {
        const value = source.getAttribute(name);
        if (value === null) this.image.removeAttribute(name);
        else if (this.image.getAttribute(name) !== value) this.image.setAttribute(name, value);
      }
      const failed = this.image.complete && this.image.naturalWidth === 0;
      this.image.hidden = failed;
      if (this.imagePlaceholder instanceof HTMLElement) this.imagePlaceholder.hidden = !failed;
      return;
    }
    this.image.removeAttribute('src');
    this.image.removeAttribute('srcset');
    this.image.alt = '';
    this.image.hidden = true;
    if (this.imagePlaceholder instanceof HTMLElement) this.imagePlaceholder.hidden = false;
  }

  updatePlanOptions(product) {
    if (!(this.planSelect instanceof HTMLSelectElement)) return;
    const previousFrequency = this.state.frequency;
    this.planSelect.replaceChildren();
    const order = ['month', 'week', 'two_weeks'];
    const plans = (Array.isArray(product.plans) ? product.plans : []).map((plan) => {
      const schedule = this.schedules.find((item) => String(item.planId) === String(plan.id));
      return schedule && order.includes(schedule.frequency) ? {...plan, ...schedule} : null;
    }).filter(Boolean).sort((a, b) => order.indexOf(a.frequency) - order.indexOf(b.frequency));

    if (plans.length === 0) {
      const option = new Option('Subscription unavailable', '');
      this.planSelect.append(option);
      this.planSelect.disabled = true;
      this.state.planId = '';
      this.state.frequency = '';
      return;
    }

    for (const plan of plans) {
      const option = new Option(plan.label, String(plan.id));
      option.dataset.frequency = plan.frequency;
      this.planSelect.append(option);
    }
    const retainedPlan = plans.find((plan) => plan.frequency === previousFrequency);
    const chosenPlan = retainedPlan || plans.find((plan) => plan.frequency === 'month') || plans[0];
    this.state.planId = String(chosenPlan.id);
    this.state.frequency = chosenPlan.frequency;
    if (previousFrequency && previousFrequency !== chosenPlan.frequency && this.status) {
      this.status.textContent = `Your previous delivery interval is unavailable for this tea. ${chosenPlan.label} is selected.`;
    }
    this.planSelect.value = this.state.planId;
    this.planSelect.disabled = false;
  }

  updatePricesAndAvailability() {
    const product = this.currentProduct();
    const plan = product?.plans?.find((candidate) => String(candidate.id) === String(this.state.planId));
    if (this.oncePrice) this.oncePrice.textContent = this.formatMoney(product?.price);
    if (this.subscriptionPrice) this.subscriptionPrice.textContent = this.formatMoney(plan?.price);

    for (const button of this.purchaseButtons) {
      if (!(button instanceof HTMLButtonElement)) continue;
      const isSubscription = button.dataset.purchase === 'subscribe';
      const enabled = this.dataset.purchasesEnabled === 'true'
        && product?.available === true
        && Number.isFinite(Number(product?.variantId))
        && (!isSubscription || Boolean(plan));
      button.disabled = !enabled || this.inFlight;
      button.setAttribute('aria-disabled', String(!enabled || this.inFlight));
    }
  }

  renderUnavailable(message) {
    if (this.status) this.status.textContent = message;
    for (const button of this.purchaseButtons) {
      if (button instanceof HTMLButtonElement) button.disabled = true;
    }
  }

  formatMoney(value) {
    if (value === null || value === undefined || !Number.isFinite(Number(value))) return '—';
    const currency = window.Shopify?.currency?.active || 'GBP';
    return new Intl.NumberFormat(document.documentElement.lang || 'en-GB', {
      style: 'currency',
      currency,
    }).format(Number(value) / 100);
  }

  async handleSubmit(event) {
    event.preventDefault();
    if (this.inFlight || !(event.submitter instanceof HTMLButtonElement)) return;

    const requestType = event.submitter.dataset.purchase;
    const product = this.currentProduct();
    const plan = product?.plans?.find((candidate) => String(candidate.id) === String(this.state.planId));
    const selection = {
      variantId: Number(product?.variantId),
      sellingPlanId: requestType === 'subscribe' ? Number(plan?.id) : null,
      requestType,
      available: product?.available === true,
    };
    ++this.cartOperation;

    if (!selection.available || !Number.isSafeInteger(selection.variantId)) {
      this.renderUnavailable('This tea and format is currently unavailable.');
      return;
    }
    if (selection.requestType === 'subscribe' && !Number.isSafeInteger(selection.sellingPlanId)) {
      this.renderUnavailable('A valid subscription delivery plan is not available for this selection.');
      return;
    }

    this.setLoading(true, event.submitter);
    try {
      const item = {id: selection.variantId, quantity: 1};
      if (selection.sellingPlanId) item.selling_plan = selection.sellingPlanId;
      if (!window.TeaDucksCartSafety) throw new Error('Your bag could not be verified. Please refresh the page.');
      // Keep the purchase lock until the shared queue has awaited Appstle and
      // confirmed actual quantities, discounts, totals and rendered sections.
      await window.TeaDucksCartSafety.mutate(this.dataset.cartAddUrl, {items: [item]});
      if (this.status) this.status.textContent = selection.requestType === 'subscribe' ? 'Subscription added to your bag.' : 'One-time purchase added to your bag.';
      this.openCartOrFallback();
    } catch (error) {
      if (this.status) this.status.textContent = error instanceof Error ? error.message : 'The item could not be added. Please try again.';
    } finally {
      this.setLoading(false);
    }
  }

  setLoading(loading, activeButton) {
    this.inFlight = loading;
    this.form?.setAttribute('aria-busy', String(loading));
    for (const button of this.purchaseButtons) button.toggleAttribute('data-loading', loading && button === activeButton);
    this.updatePricesAndAvailability();
  }

  openCartOrFallback() {
    const drawer = document.querySelector('theme-drawer#cart-drawer');
    if (drawer && typeof drawer.open === 'function') {
      drawer.open();
      return;
    }
    window.location.assign(this.dataset.cartUrl);
  }
}

if (!customElements.get('td-home')) customElements.define('td-home', TeaDucksHome);
